import { Form, Checkbox, Input, InputNumber, Radio, Select, Slider, Button, AutoComplete, Tooltip } from "antd";
import { Field, useField } from "formik";
import LocationSelect from './LocationSelect';
import { MinusCircleOutlined, PlusOutlined, HistoryOutlined } from "@ant-design/icons";
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';
import React, { useState, useEffect } from 'react';

const createField = (Component) => ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => {
      const handleChange = (value) => {
        // Handle Checkbox separately
        if (Component === Checkbox) {
          form.setFieldValue(name, value.target.checked);
          return;
        }
        // Handle Select components
        if (Component === Select || Component === Slider || Component === InputNumber) {
          form.setFieldValue(name, value);
          return;
        }
        // Handle other components
        form.setFieldValue(name, value?.target?.value ?? value);
      };

      const handleBlur = () => {
        form.setFieldTouched(name, true);
      };

      const commonProps = {
        ...props,
        placeholder: placeholder || label,
        onChange: handleChange,
        onBlur: handleBlur,
      };

      // Special handling for different components
      if (Component === Checkbox) {
        commonProps.checked = field.value;
      } else if (Component === Select) {
        commonProps.value = field.value;
      } else if (Component === Slider || Component === InputNumber) {
        commonProps.value = field.value;
      } else {
        commonProps.value = field.value;
      }

      return (
        <Form.Item
          label={label}
          validateStatus={meta.touched && meta.error ? 'error' : ''}
          help={meta.touched && meta.error}
          style={{ marginBottom: '16px' }}
        >
          <Component {...commonProps} style={{ width: '100%' }} />
        </Form.Item>
      );
    }}
  </Field>
);

export const TextField = createField(Input);

// Special version of TextField for postal code with helper text
export const PostalCodeField = ({ name, label, placeholder, ...props }) => {
  const [field, meta, helpers] = useField(name);
  
  const handleChange = (e) => {
    helpers.setValue(e.target.value);
  };
  
  const handleBlur = () => {
    helpers.setTouched(true);
  };
  
  return (
    <Form.Item
      label={label}
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={
        <div>
          {meta.touched && meta.error ? (
            <div style={{ color: '#ff4d4f' }}>{meta.error}</div>
          ) : null}
          <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
            Note: When using ZIP/Postal code search, it takes precedence over the Location field
          </div>
        </div>
      }
    >
      <Input
        {...props}
        value={field.value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder || label}
        style={{ width: '100%' }}
      />
    </Form.Item>
  );
};

// FreshnessInDays Field - New component
export const FreshnessInDaysField = ({ name, label, placeholder, ...props }) => {
  const [field, meta, helpers] = useField(name);
  
  const handleChange = (value) => {
    helpers.setValue(value);
  };
  
  const handleBlur = () => {
    helpers.setTouched(true);
  };
  
  const freshnessOptions = [
    { value: "1", label: "1 day" },
    { value: "3", label: "3 days" },
    { value: "7", label: "1 week" },
    { value: "14", label: "2 weeks" },
    { value: "30", label: "1 month" },
    { value: "90", label: "3 months" },
    { value: "180", label: "6 months" },
    { value: "365", label: "1 year" }
  ];
  
  return (
    <Form.Item
      label={label || "Resume Freshness"}
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={meta.touched && meta.error ? meta.error : "Filter resumes updated within the specified time period"}
    >
      <Select
        {...props}
        value={field.value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder || "Select resume freshness"}
        style={{ width: '100%' }}
        options={freshnessOptions}
      />
    </Form.Item>
  );
};

// Special version of TextField for keyword searches
export const KeywordField = ({ name, label, placeholder, customOnChange, ...props }) => {
  const [field, meta, helpers] = useField(name);
  const { keywords } = useSearchSuggestions();
  const [options, setOptions] = useState([]);
  const [focused, setFocused] = useState(false);
  
  // Update options when keywords change or when user types
  useEffect(() => {
    if (!keywords || !keywords.length) return;
    
    // If user has typed something, filter suggestions
    if (field.value) {
      const filteredOptions = keywords
        .filter(keyword => 
          keyword.toLowerCase().includes(field.value.toLowerCase()))
        .map(keyword => ({
          value: keyword,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{keyword}</span>
              <Tooltip title="From your search history">
                <HistoryOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </div>
          )
        }));
      setOptions(filteredOptions);
    } else if (focused) {
      // If input is empty but focused, show recent searches
      const recentOptions = keywords.slice(0, 5).map(keyword => ({
        value: keyword,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{keyword}</span>
            <Tooltip title="From your search history">
              <HistoryOutlined style={{ color: '#1890ff' }} />
            </Tooltip>
          </div>
        )
      }));
      setOptions(recentOptions);
    } else {
      setOptions([]);
    }
  }, [keywords, field.value, focused]);
  
  const handleChange = (value) => {
    helpers.setValue(value);
    
    // Call the custom onChange handler if provided
    if (customOnChange) {
      customOnChange(value);
    }
  };
  
  const handleBlur = () => {
    helpers.setTouched(true);
    setFocused(false);
  };
  
  const handleFocus = () => {
    setFocused(true);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Find the closest form and submit it
      const form = e.target.closest('form');
      if (form) {
        e.preventDefault();
        const submitEvent = new Event('submit', { cancelable: true, bubbles: true });
        form.dispatchEvent(submitEvent);
      }
    }
  };
  
  return (
    <Form.Item
      label={label}
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={meta.touched && meta.error}
    >
      <AutoComplete
        {...props}
        options={options}
        value={field.value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyPress={handleKeyPress}
        placeholder={placeholder || label}
        allowClear
        style={{ width: '100%' }}
        filterOption={false}
        notFoundContent={focused && !field.value ? "No recent searches" : "No matching suggestions"}
      />
    </Form.Item>
  );
};

// Special version of TextField for job title searches
export const JobTitleField = ({ name, label, placeholder, ...props }) => {
  const [field, meta, helpers] = useField(name);
  const { jobTitles } = useSearchSuggestions();
  const [options, setOptions] = useState([]);
  const [focused, setFocused] = useState(false);
  
  // Update options when job titles change or when user types
  useEffect(() => {
    if (!jobTitles || !jobTitles.length) return;
    
    // If user has typed something, filter suggestions
    if (field.value) {
      const filteredOptions = jobTitles
        .filter(title => 
          title.toLowerCase().includes(field.value.toLowerCase()))
        .map(title => ({
          value: title,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{title}</span>
              <Tooltip title="From your search history">
                <HistoryOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </div>
          )
        }));
      setOptions(filteredOptions);
    } else if (focused) {
      // If input is empty but focused, show recent searches
      const recentOptions = jobTitles.slice(0, 5).map(title => ({
        value: title,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{title}</span>
            <Tooltip title="From your search history">
              <HistoryOutlined style={{ color: '#1890ff' }} />
            </Tooltip>
          </div>
        )
      }));
      setOptions(recentOptions);
    } else {
      setOptions([]);
    }
  }, [jobTitles, field.value, focused]);
  
  const handleChange = (value) => {
    helpers.setValue(value);
  };
  
  const handleBlur = () => {
    helpers.setTouched(true);
    setFocused(false);
  };
  
  const handleFocus = () => {
    setFocused(true);
  };
  
  return (
    <Form.Item
      label={label}
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={meta.touched && meta.error}
    >
      <AutoComplete
        {...props}
        options={options}
        value={field.value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder || label}
        allowClear
        style={{ width: '100%' }}
        filterOption={false}
        notFoundContent={focused && !field.value ? "No recent searches" : "No matching suggestions"}
      />
    </Form.Item>
  );
};

export const SelectField = createField(({ options, ...props }) => (
  <Select {...props}>
    {options?.map(option => (
      <Select.Option key={option.value} value={option.value}>
        {option.label}
      </Select.Option>
    ))}
  </Select>
));

export const CheckboxField = createField(Checkbox);

export const RangeField = createField(Slider);

export const MultiSelectField = createField(({ options, ...props }) => (
  <Select mode="multiple" {...props}>
    {options?.map(option => (
      <Select.Option key={option.value} value={option.value}>
        {option.label}
      </Select.Option>
    ))}
  </Select>
));

export const NumberField = createField(InputNumber);

export const RadioField = createField(({ options, ...props }) => (
  <Radio.Group {...props}>
    {options?.map(option => (
      <Radio key={option.value} value={option.value}>
        {option.label}
      </Radio>
    ))}
  </Radio.Group>
));

export const LocationField = createField(LocationSelect);

export const CustomRangeField = ({ ...props }) => (
  <Input.Group compact style={{ display: 'flex', gap: '8px' }}>
    <InputNumber
      style={{ width: '100%' }}
      placeholder="Min"
      {...props}
    />
    <InputNumber
      style={{ width: '100%' }}
      placeholder="Max"
      {...props}
    />
  </Input.Group>
);

export const SalaryRangeField = ({ name, currencies, types, includeNormalized, includeSalaryNotSpecified, ...props }) => {
  const [field, meta, helpers] = useField(name);

  const handleChange = (type, value) => {
    helpers.setValue({
      ...field.value,
      [type]: value
    });
  };

  return (
    <Form.Item
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={meta.touched && meta.error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Input.Group compact style={{ display: 'flex', gap: '8px' }}>
          <Select
            style={{ width: '25%' }}
            placeholder="Currency"
            options={currencies}
            value={field.value?.currency}
            onChange={(value) => handleChange('currency', value)}
          />
          <Select
            style={{ width: '25%' }}
            placeholder="Type"
            options={types}
            value={field.value?.type}
            onChange={(value) => handleChange('type', value)}
          />
          <InputNumber
            style={{ width: '25%' }}
            placeholder="Min"
            value={field.value?.min}
            onChange={(value) => handleChange('min', value)}
          />
          <InputNumber
            style={{ width: '25%' }}
            placeholder="Max"
            value={field.value?.max}
            onChange={(value) => handleChange('max', value)}
          />
        </Input.Group>
        {includeNormalized && (
          <Input.Group compact style={{ display: 'flex', gap: '8px' }}>
            <div style={{ width: '50%', paddingLeft: '8px' }}>Normalized (Annual)</div>
            <InputNumber
              style={{ width: '25%' }}
              placeholder="Min"
              value={field.value?.normalizedMin}
              onChange={(value) => handleChange('normalizedMin', value)}
            />
            <InputNumber
              style={{ width: '25%' }}
              placeholder="Max"
              value={field.value?.normalizedMax}
              onChange={(value) => handleChange('normalizedMax', value)}
            />
          </Input.Group>
        )}
        {includeSalaryNotSpecified && (
          <Checkbox
            checked={field.value?.includeSalaryNotSpecified}
            onChange={(e) => handleChange('includeSalaryNotSpecified', e.target.checked)}
          >
            Include resumes without salary specified
          </Checkbox>
        )}
      </div>
    </Form.Item>
  );
};

export const LanguageSkillField = ({ name, proficiencyLevels, ...props }) => {
  const [field, meta, helpers] = useField(name);

  const handleAddLanguage = () => {
    const currentLanguages = field.value || [];
    helpers.setValue([...currentLanguages, { language: '', proficiency: '' }]);
  };

  const handleRemoveLanguage = (index) => {
    const newLanguages = [...field.value];
    newLanguages.splice(index, 1);
    helpers.setValue(newLanguages);
  };

  const handleLanguageChange = (index, type, value) => {
    const newLanguages = [...field.value];
    newLanguages[index] = {
      ...newLanguages[index],
      [type]: value
    };
    helpers.setValue(newLanguages);
  };

  return (
    <Form.Item
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={meta.touched && meta.error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {(field.value || []).map((language, index) => (
          <Input.Group key={index} compact style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Select
              style={{ width: '50%' }}
              placeholder="Select Language"
              value={language.language}
              onChange={(value) => handleLanguageChange(index, 'language', value)}
              options={[
                { value: "17", label: "Chinese" },
                { value: "32", label: "French" },
                { value: "39", label: "German" },
                { value: "42", label: "Hindi" },
                { value: "49", label: "Japanese" },
                { value: "82", label: "Spanish" },
              ]}
            />
            <Select
              style={{ width: '40%' }}
              placeholder="Proficiency"
              value={language.proficiency}
              onChange={(value) => handleLanguageChange(index, 'proficiency', value)}
              options={proficiencyLevels}
            />
            <Button
              type="text"
              icon={<MinusCircleOutlined />}
              onClick={() => handleRemoveLanguage(index)}
            />
          </Input.Group>
        ))}
        <Button type="dashed" onClick={handleAddLanguage} block icon={<PlusOutlined />}>
          Add Language
        </Button>
      </div>
    </Form.Item>
  );
};

// First Alternate Location Field
export const FirstAlternateLocationField = ({ name, label, placeholder, ...props }) => {
  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const value = field.value || {};
        
        // Handle sub-field changes
        const handleSubFieldChange = (subField, value) => {
          form.setFieldValue(`${name}.${subField}`, value);
        };
        
        return (
          <Form.Item
            label={label || "First Alternate Location"}
            validateStatus={meta.touched && meta.error ? "error" : ""}
            help={meta.touched && meta.error ? meta.error : ""}
            {...props}
          >
            <Input.Group compact>
              <Form.Item label="City" style={{ marginBottom: 8 }}>
                <Input 
                  placeholder="City" 
                  value={value.city || ''}
                  onChange={e => handleSubFieldChange('city', e.target.value)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="State" style={{ marginBottom: 8 }}>
                <Input 
                  placeholder="State" 
                  value={value.state || ''}
                  onChange={e => handleSubFieldChange('state', e.target.value)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="ZIP Code" style={{ marginBottom: 8 }}>
                <Input 
                  placeholder="ZIP Code" 
                  value={value.zipCode || ''}
                  onChange={e => handleSubFieldChange('zipCode', e.target.value)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Country" style={{ marginBottom: 0 }}>
                <Select
                  placeholder="Country"
                  value={value.country || 'US'}
                  onChange={val => handleSubFieldChange('country', val)}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'US', label: 'United States' },
                    { value: 'CA', label: 'Canada' },
                    { value: 'UK', label: 'United Kingdom' },
                    // Add more countries as needed
                  ]}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        );
      }}
    </Field>
  );
};

// Second Alternate Location Field (similar to First)
export const SecondAlternateLocationField = ({ name, label, placeholder, ...props }) => {
  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const value = field.value || {};
        
        // Handle sub-field changes
        const handleSubFieldChange = (subField, value) => {
          form.setFieldValue(`${name}.${subField}`, value);
        };
        
        return (
          <Form.Item
            label={label || "Second Alternate Location"}
            validateStatus={meta.touched && meta.error ? "error" : ""}
            help={meta.touched && meta.error ? meta.error : ""}
            {...props}
          >
            <Input.Group compact>
              <Form.Item label="City" style={{ marginBottom: 8 }}>
                <Input 
                  placeholder="City" 
                  value={value.city || ''}
                  onChange={e => handleSubFieldChange('city', e.target.value)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="State" style={{ marginBottom: 8 }}>
                <Input 
                  placeholder="State" 
                  value={value.state || ''}
                  onChange={e => handleSubFieldChange('state', e.target.value)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="ZIP Code" style={{ marginBottom: 8 }}>
                <Input 
                  placeholder="ZIP Code" 
                  value={value.zipCode || ''}
                  onChange={e => handleSubFieldChange('zipCode', e.target.value)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Country" style={{ marginBottom: 0 }}>
                <Select
                  placeholder="Country"
                  value={value.country || 'US'}
                  onChange={val => handleSubFieldChange('country', val)}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'US', label: 'United States' },
                    { value: 'CA', label: 'Canada' },
                    { value: 'UK', label: 'United Kingdom' },
                    // Add more countries as needed
                  ]}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        );
      }}
    </Field>
  );
};

// Maximum Commute Field
export const MaximumCommuteField = ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        label={label || "Maximum Commute (minutes)"}
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <InputNumber
          min={0}
          max={180}
          value={field.value}
          onChange={value => form.setFieldValue(name, value)}
          style={{ width: "100%" }}
          placeholder={placeholder || "Enter maximum commute time"}
        />
      </Form.Item>
    )}
  </Field>
);

// Minimum Degree Field
export const MinimumDegreeField = ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        label={label || "Minimum Degree"}
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <Select
          value={field.value}
          onChange={value => form.setFieldValue(name, value)}
          placeholder={placeholder || "Select minimum degree"}
          style={{ width: "100%" }}
          options={[
            { value: 'CE321', label: 'High School Diploma' },
            { value: 'CE322', label: 'Associate Degree' },
            { value: 'CE323', label: 'Bachelor\'s Degree' },
            { value: 'CE324', label: 'Master\'s Degree' },
            { value: 'CE325', label: 'Doctorate Degree' },
            { value: 'CE326', label: 'Professional Degree' },
            { value: 'CE327', label: 'Some College' },
            { value: 'CE328', label: 'Vocational Degree' }
          ]}
        />
      </Form.Item>
    )}
  </Field>
);

// Employment Type Field
export const EmploymentTypeField = ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        label={label || "Employment Type"}
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <Select
          mode="multiple"
          value={field.value || []}
          onChange={value => form.setFieldValue(name, value)}
          placeholder={placeholder || "Select employment types"}
          style={{ width: "100%" }}
          options={[
            { value: 'ETFE', label: 'Full-Time Employee' },
            { value: 'ETPE', label: 'Part-Time Employee' },
            { value: 'ETCT', label: 'Contract/Temporary' },
            { value: 'ETTE', label: 'Temporary Employee' },
            { value: 'ETCO', label: 'Contract' },
            { value: 'ETIN', label: 'Internship' },
            { value: 'ETVO', label: 'Volunteer' },
            { value: 'ETSE', label: 'Seasonal' }
          ]}
        />
      </Form.Item>
    )}
  </Field>
);

// Minimum Travel Requirement Field
export const MinimumTravelRequirementField = ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        label={label || "Minimum Travel Requirement"}
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <Select
          value={field.value}
          onChange={value => form.setFieldValue(name, value)}
          placeholder={placeholder || "Select travel requirement"}
          style={{ width: "100%" }}
          options={[
            { value: 'RTNONE', label: 'No Travel' },
            { value: 'RTMINM', label: 'Minimal Travel' },
            { value: '25PCNT', label: '25% Travel' },
            { value: '50PCNT', label: '50% Travel' },
            { value: '75PCNT', label: '75% Travel' },
            { value: '100PCNT', label: '100% Travel' }
          ]}
        />
      </Form.Item>
    )}
  </Field>
);

// Compensation Type Field
export const CompensationTypeField = ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        label={label || "Compensation Type"}
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <Select
          value={field.value}
          onChange={value => form.setFieldValue(name, value)}
          placeholder={placeholder || "Select compensation type"}
          style={{ width: "100%" }}
          options={[
            { value: 'SALR', label: 'Salary' },
            { value: 'HRLY', label: 'Hourly' },
            { value: 'MLED', label: 'Annual' }
          ]}
        />
      </Form.Item>
    )}
  </Field>
);

// Minimum and Maximum Salary Fields
export const MinimumSalaryField = ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        label={label || "Minimum Salary"}
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <InputNumber
          min={0}
          value={field.value}
          onChange={value => form.setFieldValue(name, value)}
          style={{ width: "100%" }}
          placeholder={placeholder || "Enter minimum salary"}
          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => value.replace(/\$\s?|(,*)/g, '')}
        />
      </Form.Item>
    )}
  </Field>
);

export const MaximumSalaryField = ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        label={label || "Maximum Salary"}
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <InputNumber
          min={0}
          value={field.value}
          onChange={value => form.setFieldValue(name, value)}
          style={{ width: "100%" }}
          placeholder={placeholder || "Enter maximum salary"}
          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => value.replace(/\$\s?|(,*)/g, '')}
        />
      </Form.Item>
    )}
  </Field>
);

// Exclude Resumes With No Salary Field
export const ExcludeResumesWithNoSalaryField = ({ name, label, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <Checkbox
          checked={field.value}
          onChange={e => form.setFieldValue(name, e.target.checked)}
        >
          {label || "Exclude Resumes With No Salary"}
        </Checkbox>
      </Form.Item>
    )}
  </Field>
);

// Languages Spoken Field
export const LanguagesSpokenField = ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        label={label || "Languages Spoken"}
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <Select
          mode="multiple"
          value={field.value || []}
          onChange={value => form.setFieldValue(name, value)}
          placeholder={placeholder || "Select languages"}
          style={{ width: "100%" }}
          options={[
            { value: 'LAEN', label: 'English' },
            { value: 'LASP', label: 'Spanish' },
            { value: 'LAFR', label: 'French' },
            { value: 'LADE', label: 'German' },
            { value: 'LAIT', label: 'Italian' },
            { value: 'LAJA', label: 'Japanese' },
            { value: 'LAKO', label: 'Korean' },
            { value: 'LAZH', label: 'Chinese' },
            { value: 'LARU', label: 'Russian' },
            { value: 'LAPT', label: 'Portuguese' },
            { value: 'LAAR', label: 'Arabic' },
            { value: 'LAIN', label: 'Hindi' }
          ]}
        />
      </Form.Item>
    )}
  </Field>
);

// Currently Employed Field
export const CurrentlyEmployedField = ({ name, label, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <Checkbox
          checked={field.value}
          onChange={e => form.setFieldValue(name, e.target.checked)}
        >
          {label || "Currently Employed"}
        </Checkbox>
      </Form.Item>
    )}
  </Field>
);

// Minimum Employees Managed Field
export const MinimumEmployeesManagedField = ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        label={label || "Minimum Employees Managed"}
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <InputNumber
          min={0}
          value={field.value}
          onChange={value => form.setFieldValue(name, value)}
          style={{ width: "100%" }}
          placeholder={placeholder || "Enter minimum number of employees"}
        />
      </Form.Item>
    )}
  </Field>
);

// Exclude IVR Resumes Field
export const ExcludeIVRResumesField = ({ name, label, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : ""}
        {...props}
      >
        <Checkbox
          checked={field.value}
          onChange={e => form.setFieldValue(name, e.target.checked)}
        >
          {label || "Exclude IVR Resumes"}
        </Checkbox>
      </Form.Item>
    )}
  </Field>
);

// OrderBy/SortBy Field
export const SortByField = ({ name, label, placeholder, ...props }) => {
  // Import orderByOptions directly to ensure we're using the values expected by CB API
  const orderByOptions = [
    { value: "+RECENTYEARLYPAY", label: "Most Recent Pay (ascending)" },
    { value: "-RECENTYEARLYPAY", label: "Most Recent Pay (descending)" },
    { value: "+MODIFIEDINT", label: "Freshness (ascending)" },
    { value: "-MODIFIEDINT", label: "Freshness (descending)" },
    { value: "+RELV", label: "Relevance (ascending)" },
    { value: "-RELV", label: "Relevance (descending)" }
  ];

  return (
    <Field name={name}>
      {({ field, form, meta }) => (
        <Form.Item
          label={label || "Sort Results By"}
          validateStatus={meta.touched && meta.error ? "error" : ""}
          help={meta.touched && meta.error ? meta.error : ""}
          {...props}
        >
          <Select
            value={field.value}
            onChange={value => form.setFieldValue(name, value)}
            placeholder={placeholder || "Select sorting order"}
            style={{ width: "100%" }}
            options={orderByOptions}
          />
        </Form.Item>
      )}
    </Field>
  );
};

// KeywordBehavior Field
export const KeywordBehaviorField = ({ name, label, placeholder, ...props }) => {
  return (
    <Field name={name}>
      {({ field, form, meta }) => (
        <Form.Item
          label={label || "Keyword Search Type"}
          validateStatus={meta.touched && meta.error ? "error" : ""}
          help={meta.touched && meta.error ? 
            meta.error : 
            <div>
              <p>Select how keywords should be matched:</p>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>AND - Match all words (default)</li>
                <li>OR - Match any word</li>
                <li>Boolean - Use AND, OR, NOT, *, etc.</li>
              </ul>
            </div>
          }
          {...props}
        >
          <Select
            value={field.value}
            onChange={value => form.setFieldValue(name, value)}
            placeholder={placeholder || "Select keyword behavior"}
            style={{ width: "100%" }}
            options={[
              // These values match the CB API expectations in processSearchQueryParams function
              { value: "and", label: "Match All Words (AND)" },
              { value: "or", label: "Match Any Word (OR)" },
              { value: "boolean", label: "Boolean Query" },
            ]}
          />
        </Form.Item>
      )}
    </Field>
  );
};

// NicheInclusion Field - based on CareerBuilder documentation
export const NicheInclusionField = ({ name, label, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => (
      <Form.Item
        validateStatus={meta.touched && meta.error ? "error" : ""}
        help={meta.touched && meta.error ? meta.error : 
          "Include candidates from specific CareerBuilder partner networks"}
        {...props}
      >
        <Checkbox
          checked={field.value}
          onChange={e => form.setFieldValue(name, e.target.checked)}
        >
          {label || "Include Niche Networks"}
        </Checkbox>
        
        {field.value && (
          <Select
            mode="multiple"
            style={{ marginTop: '8px', width: '100%' }}
            placeholder="Select specific niche networks (optional)"
            onChange={values => form.setFieldValue(`${name}Values`, values)}
            value={form.values[`${name}Values`] || []}
            options={[
              { value: "CC", label: "CareerRookie" },
              { value: "SF", label: "StaffNurse" },
              { value: "SG", label: "Sologig" },
              { value: "UG", label: "CareerBuilder & Sologig, combined" },
              { value: "WM", label: "Miracle Workers" },
              { value: "WR", label: "Work In Retail" },
              { value: "WW", label: "Wounded Warrior" },
              { value: "RX", label: "Recrulex" },
              { value: "LJ", label: "Les Jeudis" },
              { value: "CE", label: "CAO Emplois" },
              { value: "ER", label: "E-recrut" },
              { value: "JS", label: "Job Scout" },
              { value: "CY", label: "Cyprus" }
            ]}
          />
        )}
      </Form.Item>
    )}
  </Field>
);


